import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ReportCallDirections } from '@ndt/report-output/shared/models/call-direction-report-parameter';
import { defaultAllMultiEntitySelectParameter } from '@ndt/report-output/shared/models/multi-entity-selection-report-parameter';
import { NumberOfCallersReportParameter } from '@ndt/report-output/shared/models/number-of-callers-report-parameter';
import { ReportSortBy, SortByReportParameter } from '@ndt/report-output/shared/models/sort-by-report-parameter';
import { TimeZoneReportParameter } from '@ndt/report-output/shared/models/time-zone-report-parameters';
import { TopCallersReportParameters } from '@ndt/report-output/top-callers/top-callers';
import { createDateRangeReportParameter } from '@ndt/reports/shared/models/date-range-report-parameter-value';
import { defaultNumberOfCallersParameter } from '@ndt/reports/shared/models/number-of-callers-report-parameter-value';
import { defaultTimeZoneParameter } from '@ndt/reports/shared/models/time-zone-parameter-value';
import {
   ParameterFormValueUnion,
   convertFromFormValueList,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-form-value';
import {
   CallDirectionsToIncludeReportParameter,
   DaysOfWeekReportParameter,
   EntitiesToIncludeParameter,
   ReportParametersUnion,
   convertToDateRangeReportParameter,
   convertToForm,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-union.model';
import { ReportParameterConfig } from '@ndt/reports/shared/report-parameter-list/report-parameter.config';
import {
   ReportParameterFormService,
   ReportParameterFormServiceBase,
} from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form.service';
import { ReportParameters } from 'generated/graphql-types';
import { LcsDateTimeAdapter } from 'src/app/common-components/date-time-picker/configuration/date-time-adapter';
import { throwIfNullOrUndefined } from 'src/app/common-components/utils/type-safety';

export class TopCallersParametersFormService
   extends ReportParameterFormServiceBase
   implements ReportParameterFormService<TopCallersReportParameters>
{
   config: ReportParameterConfig[];

   form: UntypedFormGroup;

   constructor(fb: UntypedFormBuilder, private dateAdapter: LcsDateTimeAdapter<Date>) {
      super(fb);
      const initialValue: ReportParametersUnion[] = [
         {
            accountId: 0,
            all: true,
            names: [],
            parameterType: ReportParameters.PhoneNumbersToInclude,
            sortOrder: 4,
            values: [],
         },
         {
            parameterType: ReportParameters.DateRange,
            ...createDateRangeReportParameter(),
            sortOrder: 2,
         },
         {
            parameterType: ReportParameters.CallDirectionsToInclude,
            sortOrder: 3,
            value: ReportCallDirections.Both,
         },
         {
            parameterType: ReportParameters.TimeZone,
            sortOrder: 6,
            value: '',
         },
         {
            parameterType: ReportParameters.NumberOfCallers,
            sortOrder: 7,
            value: 50,
         },
         {
            parameterType: ReportParameters.SortBy,
            sortOrder: 8,
            value: ReportSortBy.Duration,
         },
      ];

      this.config = initialValue.map((ap) => {
         return this.getReportParameterConfig(ap.parameterType);
      });

      this.form = convertToForm(initialValue, fb);
   }
   isListComplete(values: ReportParametersUnion[], includeAskParameters?: boolean | undefined): boolean {
      const dateRange = values.find((v) => v.parameterType === ReportParameters.DateRange);
      return !!dateRange;
   }
   patchSavedValues(values: ReportParametersUnion[], showAskToggle: boolean, showAskParameters: boolean): void {
      throw new Error('Method not implemented.');
   }
   setValue(values: ParameterFormValueUnion[]): void {
      this.form.setValue(values, { emitEvent: false });
   }
   getValue(includeAskParameters?: boolean | undefined): TopCallersReportParameters | null {
      const formValue = this.form.getRawValue();
      const values: ParameterFormValueUnion[] = formValue.values;
      const convertedValues = convertFromFormValueList(values);
      return this.convertValue(convertedValues);
   }
   convertValue(values: ReportParametersUnion[]): TopCallersReportParameters | null {
      const extensions = values.find((v) => {
         return v.parameterType === ReportParameters.ExtensionsToInclude;
      }) as EntitiesToIncludeParameter | undefined;

      const phoneNumbers = values.find((v) => {
         return v.parameterType === ReportParameters.PhoneNumbersToInclude;
      }) as EntitiesToIncludeParameter | undefined;

      const callDirectionsToInclude = values.find((v) => {
         return v.parameterType === ReportParameters.CallDirectionsToInclude;
      }) as CallDirectionsToIncludeReportParameter | undefined;

      const daysOfWeek = values.find((v) => {
         return v.parameterType === ReportParameters.DaysOfTheWeek;
      }) as DaysOfWeekReportParameter | undefined;

      const timeZone = values.find((v) => {
         return v.parameterType === ReportParameters.TimeZone;
      }) as TimeZoneReportParameter | undefined;

      const numberOfCallers = values.find((v) => {
         return v.parameterType === ReportParameters.NumberOfCallers;
      }) as NumberOfCallersReportParameter | undefined;

      const sortBy = values.find((v) => {
         return v.parameterType === ReportParameters.SortBy;
      }) as SortByReportParameter | undefined;

      const output: TopCallersReportParameters = {
         dateRange: throwIfNullOrUndefined(convertToDateRangeReportParameter(values, this.dateAdapter)),
         callDirection: callDirectionsToInclude
            ? {
                 value: callDirectionsToInclude.value,
              }
            : {
                 value: ReportCallDirections.Both,
              },
         phoneNumbers: phoneNumbers
            ? {
                 accountId: phoneNumbers.accountId,
                 all: !!phoneNumbers?.all,
                 values: phoneNumbers.values || [],
              }
            : defaultAllMultiEntitySelectParameter,
         timeZone: timeZone
            ? {
                 value: timeZone.value,
              }
            : defaultTimeZoneParameter,
         numberOfCallers: numberOfCallers
            ? {
                 value: numberOfCallers.value,
              }
            : defaultNumberOfCallersParameter,
         sortBy: sortBy
            ? {
                 value: sortBy.value,
              }
            : {
                 value: ReportSortBy.Duration,
              },
      };

      return output;
   }
}
